import { planFilterForCase } from '~/utils/planDays'

/**
 * /company-cases ARRIVAL — the pure pieces that keep the "Loading…" overlay
 * short (Sandy 2026-10-05: "it can't be this slow").
 *
 *   - The caseload PREFETCH: on a deep-linked arrival the server already said
 *     which company / lead agency the case lives in, so the case list is
 *     requested WHILE the company bar loads instead of after it. The page's
 *     regular load then takes that in-flight request — only if it asked for
 *     exactly the same thing (company, agency, status, View-As); anything else
 *     fetches normally.
 *   - Lifting the overlay when the notes pane has ACTUALLY loaded, instead of
 *     after a fixed pause.
 *   - The arrival's decisions (/company-cases refactor, 2026-10-05, behaviour
 *     unchanged): why a case can't be shown, the day to land on, the View-As
 *     peer scope, and which empty state shows.
 */

/** The identity of a caseload request — what must match to reuse a prefetch. */
export function activeCasesKey({ companyId, leadAgencyId = null, caseStageCategory = 'active', viewAsUserId = null }) {
  return [companyId, leadAgencyId, caseStageCategory, viewAsUserId]
    .map(v => (v == null ? '' : String(v)))
    .join('|')
}

/**
 * One slot for one in-flight prefetch. `take(key)` hands the promise over once
 * when the key matches, and empties the slot either way (a stale prefetch is
 * never reused later).
 */
export function createPrefetchSlot() {
  let slot = null

  return {
    put(key, promise) {
      slot = { key, promise }
    },
    take(key) {
      const hit = slot && slot.key === key ? slot.promise : null

      slot = null

      return hit
    },
  }
}

/**
 * Resolves once a loading flag has gone true and back to false — or right away
 * if it never turns true within `startWithinMs` (nothing to wait for), and in
 * any case after `maxMs` (the overlay must never stick). `isLoading` is read by
 * polling so it works with any ref / exposed component state.
 */
export function waitUntilLoaded(isLoading, { startWithinMs = 300, maxMs = 4000, stepMs = 25 } = {}) {
  return new Promise(resolve => {
    const t0 = Date.now()
    let started = false

    const tick = () => {
      const loading = !!isLoading()
      const elapsed = Date.now() - t0

      if (loading) started = true
      if ((started && !loading) || (!started && elapsed >= startWithinMs) || elapsed >= maxMs) {
        resolve()

        return
      }
      setTimeout(tick, stepMs)
    }

    tick()
  })
}

/**
 * WHY a deep-linked case can't be shown, from the server's `followupsCaseTarget`
 * answer — or null when it IS reachable (it just isn't in the scope on screen,
 * the arrival is already moving there, and a warning would be noise).
 */
export const MISS_REASONS = {
  removed: 'That case is in the trash.',
  company: "That case's company is inactive.",
  agency: "That case's lead agency is inactive.",
  access: "That case isn't in your access.",
  decided: 'That case is already closed — open it from the inbox to reactivate.',
}

export function missReasonOf(target) {
  if (target?.reachable) return null

  return MISS_REASONS[target?.reason] ?? "That case can't be opened here."
}

/**
 * The planning day an arrival lands on. A deep-linked case (from /cases with an
 * assignee, or from anywhere else) lands on its own day for the viewer — its
 * planned day, Future, or "All" without a plan — so it is ALWAYS visible. A
 * plain visit, or a case that isn't loaded, stays on "All".
 */
export function arrivalPlanFilter(target, { isAssigneeHandoff, isForeignCaseDeepLink }) {
  if (target && (isAssigneeHandoff || isForeignCaseDeepLink)) return planFilterForCase(target)

  return { kind: 'all', iso: null }
}

/** The View-As scope for a peer picked by an arrival: label → short name → name. */
export const peerScopeOf = peer => ({
  userId: peer.id,
  label: peer.label || peer.shortName || peer.name || '',
  color: peer.color ?? null,
})

/**
 * Which empty state shows when nothing is displayed: no active cases at all →
 * 'no-cases'; cases exist but the toolbar filters hide them all → 'no-match';
 * otherwise the planning day is empty → 'empty-day'.
 */
export function emptyVariantOf(activeCases, hasToolbarFilters) {
  if (!activeCases || activeCases.length === 0) return 'no-cases'
  if (hasToolbarFilters) return 'no-match'

  return 'empty-day'
}
